import React from 'react';
import { colors } from '@/assets/styles/colors';


export type SpinnerSize = 'sm' | 'md' | 'lg';
export type SpinnerColor = 'primary' | 'white' | 'gray' | 'important';

interface SpinnerProps {
  size?: SpinnerSize;
  color?: SpinnerColor;
  className?: string;
  'data-testid'?: string;
}

const SIZE_MAP: Record<SpinnerSize, number> = {
  sm: 20,
  md: 24,
  lg: 32,
};

const COLOR_MAP: Record<SpinnerColor, string> = {
  primary: colors.primary.main,
  white: colors.grays.neutralFF,
  gray: colors.grays.neutralCC,
  important: colors.important.main,
};

/**
 * Spinner - Animated loading spinner
 * 
 * @description
 * Unified spinner component with configurable size and color.
 * Used for inline loading states in buttons, selectors, and other UI elements.
 * 
 * @example
 * // Small primary spinner
 * <Spinner size="sm" />
 * 
 * @example
 * // Large white spinner for dark backgrounds
 * <Spinner size="lg" color="white" />
 */
export const Spinner: React.FC<SpinnerProps> = ({
  size = 'md',
  color = 'primary',
  className = '',
  'data-testid': dataTestId,
}) => {
  const spinnerSize = SIZE_MAP[size];
  const spinnerColor = COLOR_MAP[color];

  return (
    <div
      className={`animate-spin rounded-full ${className}`}
      style={{
        width: `${spinnerSize}px`,
        height: `${spinnerSize}px`,
        border: `2px solid ${spinnerColor}`,
        borderTopColor: 'transparent',
      }}
      data-testid={dataTestId}
      aria-label="Loading"
      role="status"
    />
  );
};

export default Spinner;
